import { Module } from "vuex";
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/storage';
import 'firebase/functions';
import { serializer } from "@/api/shared/serializer";
import { getRegionName } from "@/utils/functionsRegion";

interface ScreenshotData {
  deviceId: string;
  facilityId: string;
  facilityName?: string;
  deviceName?: string;
  isOnline: boolean;
  captureStatus: 'success' | 'error' | 'pending';
  lastCaptured?: any;
  thumbnailUrl?: string;
  fullImageUrl?: string;
  error?: string;
}

const digitalSignageScreenshotsModule: Module<any, any> = {
  namespaced: false,
  state: {
    screenshots: [] as ScreenshotData[],
    loading: false,
    fetchingScreenshots: false,
    lastRefreshAt: null as null | Date,
    captureStatus: 'idle' as 'idle' | 'running' | 'done' | 'error',
    captureError: null as null | string,
    loadError: null as null | string,
  },

  mutations: {
    setScreenshots(state: any, payload: ScreenshotData[]) {
      state.screenshots = payload;
    },
    setLoading(state: any, payload: boolean) {
      state.loading = payload;
    },
    setFetchingScreenshots(state: any, payload: boolean) {
      state.fetchingScreenshots = payload;
    },
    setLastRefreshAt(state: any, payload: Date | null) {
      state.lastRefreshAt = payload;
    },
    setCaptureStatus(state: any, payload: 'idle' | 'running' | 'done' | 'error') {
      state.captureStatus = payload;
    },
    setCaptureError(state: any, payload: string | null) {
      state.captureError = payload;
    },
    setLoadError(state: any, payload: string | null) {
      state.loadError = payload;
    },
  },

  actions: {
    async getDigitalSignageScreenshots({ commit }) {
      commit('setLoading', true);
      commit('setLoadError', null);

      try {
        // Query Firestore collection 'digitalSignageScreenshots'
        const screenshotsSnap = await firebase
          .firestore()
          .collection("digitalSignageScreenshots")
          .get();

        const rawDocs = serializer.querySnapshotToDocuments(screenshotsSnap) as Array<Record<string, any>>;
        const firstDoc = rawDocs[0] || {};
        const screenshots = rawDocs.length === 1 && !firstDoc.deviceId && !firstDoc.identifier
          ? Object.entries(firstDoc)
              .filter(([key, value]) => key !== 'id' && value && typeof value === 'object')
              .map(([key, value]) => ({
                id: key,
                ...(value as Record<string, any>),
              }))
          : rawDocs;

        // Fetch thumbnail URLs from Firebase Storage
        const screenshotsWithUrls = await Promise.all(
          screenshots.map(async (screenshot: any) => {
            const deviceId = screenshot.deviceId || screenshot.identifier || screenshot.displayId || screenshot.id;
            const storagePath = screenshot.storagePath || `digital-signage-screenshots/${deviceId}/latest.jpg`;
            const basePath = storagePath.replace(/\.jpg$/i, '');
            const thumbnailPath = `${basePath}_200x200.jpg`;
            const fullImagePath = storagePath;
            let thumbnailUrl: string | null = null;
            let fullImageUrl: string | null = null;
            const lastCaptured = screenshot.lastCapturedAt?.__time__
              ? new Date(screenshot.lastCapturedAt.__time__)
              : screenshot.lastCapturedAt || screenshot.lastCaptured;

            try {
              thumbnailUrl = await firebase.storage().ref().child(thumbnailPath).getDownloadURL();
            } catch (error) {
              console.error(`Error fetching thumbnail for device ${screenshot.deviceId}:`, error);
            }

            try {
              fullImageUrl = await firebase.storage().ref().child(fullImagePath).getDownloadURL();
            } catch (error) {
              console.error(`Error fetching full image for device ${screenshot.deviceId}:`, error);
            }

            return {
              ...screenshot,
              deviceId,
              deviceName: screenshot.deviceName || screenshot.name,
              isOnline: screenshot.isOnline ?? screenshot.deviceOnline,
              captureStatus: screenshot.captureStatus || screenshot.status,
              lastCaptured,
              thumbnailUrl,
              fullImageUrl,
              error: thumbnailUrl || fullImageUrl ? screenshot.error : (screenshot.error || 'Image not available'),
            };
          })
        );

        commit('setScreenshots', screenshotsWithUrls);
        commit('setLastRefreshAt', new Date());
      } catch (error) {
        console.error('Error fetching screenshots:', error);
        commit('setLoadError', 'Error loading screenshots');
      } finally {
        commit('setLoading', false);
      }
    },

    async captureDeviceScreenshots({ commit, dispatch }) {
      commit('setFetchingScreenshots', true);
      commit('setCaptureStatus', 'running');
      commit('setCaptureError', null);

      try {
        const result = await firebase
          .app()
          .functions(getRegionName())
          .httpsCallable('digitalSignageScreenshots-captureCallable')({});

        if (result.data?.success) {
          commit('setCaptureStatus', 'done');
          await dispatch('getDigitalSignageScreenshots');
        } else {
          throw new Error(result.data.error || 'Failed to capture screenshots');
        }
      } catch (error) {
        console.error('Error capturing screenshots:', error);
        commit('setCaptureStatus', 'error');
        commit('setCaptureError', (error as Error).message || 'Error capturing screenshots');
      } finally {
        commit('setFetchingScreenshots', false);
      }
    },
  },
};

export default digitalSignageScreenshotsModule;
